/** @jsx jsx */

/** @jsxFrag React.Fragment */
import { jsx } from '@emotion/react';

import * as React from 'react';

import _ from 'lodash';

import { Grid, Typography2 } from '@coursera/cds-core';
import { Checkbox } from '@coursera/coursera-ui';

import FilterListSelectAllOption from 'bundles/enterprise-admin-drawers/components/components/FilterListSelectAllOption';
import type { FilterOption, FilterType } from 'bundles/enterprise-admin-drawers/components/types';
import { getFilterOptionTypeName } from 'bundles/enterprise-admin-messages/constants';
import type { FilterOptionType } from 'bundles/enterprise-admin-messages/types';

import _t from 'i18n!nls/enterprise-admin-drawers';

export type Props = {
  filterOptions: FilterOption[];
  filterType: FilterType;
  selectedFilterOptions: FilterOption[];
  emptyText?: string;
  onChangeSelectAll?: (nextValue: FilterOption[]) => void;
  onChange: (filterOption: FilterOption, checked: boolean) => void;
  loading?: boolean;
  className?: string;
  preSorted?: boolean;
  header?: JSX.Element;
  listLabel?: string;
};

const FilterOptionList = ({
  filterOptions,
  filterType,
  selectedFilterOptions,
  emptyText = _t('No results.'),
  onChangeSelectAll,
  onChange,
  loading,
  className,
  preSorted,
  header,
  listLabel = _t('Name'),
}: Props) => {
  const isFilterOptionChecked = (filterId: string): boolean => _.some(selectedFilterOptions, (f) => f.id === filterId);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const filterId = e.target.value;
    const filterOption = _.find(filterOptions, (fo) => fo.id === filterId);

    if (filterOption) {
      const isChecked = isFilterOptionChecked(filterId);
      onChange(filterOption, !isChecked);
    }
  };

  if (loading) {
    return (
      <div className="rc-FilterOptionList">
        <div className="c-loading">{_t('Loading...')}</div>
      </div>
    );
  }

  return (
    <div className={`rc-FilterOptionList vertical-box ${className}`}>
      {filterOptions.length === 0 && <div className="c-emptyText">{emptyText}</div>}
      {filterOptions.length > 0 && (
        <Grid className="c-FilterOptionListHeader">
          {header}
          <Grid container>
            <Grid item xs={8}>
              <Typography2 component="h3" variant="subtitleMedium">
                {listLabel}
              </Typography2>
            </Grid>
            <Grid item xs={4}>
              {filterType === 'ENROLLMENTS' && (
                <Typography2 component="h3" variant="subtitleMedium">
                  {_t('Type')}
                </Typography2>
              )}
            </Grid>
          </Grid>
        </Grid>
      )}
      {filterOptions.length > 0 && (
        <div className="c-FilterOptionList flex-1 mh-0 font-sm">
          {onChangeSelectAll && (
            <FilterListSelectAllOption
              onChange={onChangeSelectAll}
              filterOptions={filterOptions}
              selectedFilterOptions={selectedFilterOptions}
            />
          )}

          {(preSorted ? filterOptions : filterOptions.sort((a, b) => (a.label > b.label ? 1 : -1))).map((fo) => (
            <div className="c-filterOption" key={fo.id}>
              <div className="horizontal-box w-100">
                <div className="flex-2">
                  <Checkbox checked={isFilterOptionChecked(fo.id)} value={fo.id} onChange={handleChange} size={20}>
                    <span>{fo.label}</span>
                  </Checkbox>
                </div>

                {filterType === 'ENROLLMENTS' && (
                  <div className="flex-1">{getFilterOptionTypeName(fo.type as FilterOptionType).full}</div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default FilterOptionList;
